// Which principal this browser is recording, and what has to happen the moment it changes.
//
// This lives apart from the session cache because of WHEN it has to run, not because of
// what it does. The cache is initialized from the deferred account bundle, but the layout
// identifies the current user to LogRocket - and registers a trusted behavior capture for
// them - in a synchronous <head> script that executes long before that bundle does.
// Rotating inside the cache initializer therefore rotated too late: on a shared browser,
// user B was already identified onto user A's still-running recording, and B's capture
// callback could resolve first and attach A's session URL to B's loan. Neither can be
// taken back afterwards, so the rotation has to be able to run on its own, ahead of
// identify, from a page that has loaded nothing else yet.
//
// It also deliberately knows nothing about loans. A signed-in principal with no loan is
// still identified by the layout, so it still has to be able to end the previous person's
// recording - requiring a loan id here is what made that case a no-op.

/** The storage namespace every marketplace session entry lives under, whatever the loan. */
export const LOGROCKET_SESSION_CACHE_KEY_PREFIX = 'marketplace:logrocket-session:v3';

/** The principal this browser last recorded, so an account switch can be detected. */
export const LOGROCKET_PRINCIPAL_KEY = 'marketplace:logrocket-principal:v1';

/**
 * How one page's rotation attempt resolved. UNCHANGED means the boundary needed no work
 * (same principal or nobody signed in); ROTATED means the recording was really ended;
 * ROTATION_FAILED means a switch was required but the SDK could not end the recording;
 * UNTRACKABLE means durable storage could not prove whose recording is active.
 */
export const PRINCIPAL_UNCHANGED = 'unchanged';
export const PRINCIPAL_ROTATED = 'rotated';
export const PRINCIPAL_ROTATION_FAILED = 'required_but_failed';
export const PRINCIPAL_UNTRACKABLE = 'untrackable';

/**
 * The only states that prove principal evidence can be attached on this document.
 * Unknown, failed and storage-untrackable states fail closed; navigation remains
 * unaffected, but identify, URL capture, cache writes and active-tab answers do not run.
 */
export function canCapturePrincipalEvidence(state) {
    return state === PRINCIPAL_UNCHANGED || state === PRINCIPAL_ROTATED;
}

/**
 * A positive integer identifier, as a string. Anything else - 0, a float, a crafted
 * string, an object - is not an identifier and must not become a storage key or a
 * principal marker.
 */
export function normalizeLogRocketId(value) {
    if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
        return String(value);
    }

    if (typeof value === 'string' && /^[1-9]\d*$/.test(value)) {
        return value;
    }

    return null;
}

/**
 * The browser's own durable localStorage, or null when nothing durable exists.
 *
 * `window.localStorage` being present and functional is not enough to hold the principal
 * marker: when native storage is unusable, the layout's #5873 shim replaces it with a
 * truthy in-memory object so unrelated code keeps working. That replacement lives and
 * dies with the document - to this module it is the absence of storage wearing storage's
 * interface. Treating it as durable made every page load look like an unknown previous
 * principal, so the rotation cut the recording on every navigation for exactly the
 * cookie-blocked segment the shim exists for. Only an instance of the runtime's own
 * Storage interface can carry a marker across documents; anything else reads as "no
 * storage", which every caller already treats as UNTRACKABLE and fails closed.
 */
export function durableLocalStorage(runtime) {
    try {
        const storage = runtime?.localStorage || null;

        if (!storage) {
            return null;
        }

        const StorageInterface = runtime?.Storage;

        if (typeof StorageInterface !== 'function' || !(storage instanceof StorageInterface)) {
            return null;
        }

        return storage;
    } catch (error) {
        return null;
    }
}

export function resolveLogRocketPrincipalState(runtime, storage, logRocket, userId) {
    if (!runtime) {
        return undefined;
    }

    try {
        // The synchronous head entry and deferred account bundle share this document.
        // Latch BEFORE cutting: if the cut succeeds but its marker write fails, the
        // deferred initializer must reuse ROTATED rather than cut again and clear the
        // identity the layout just established.
        if (runtime.marketplaceLogRocketPrincipalRotationAttempted === true) {
            return runtime.marketplaceLogRocketPrincipalState;
        }

        runtime.marketplaceLogRocketPrincipalRotationAttempted = true;
    } catch (error) {
        return undefined;
    }

    let state;

    try {
        state = rotateLogRocketPrincipalIfChanged(storage, logRocket, userId);
    } catch (error) {
        // An unknown outcome cannot authorize evidence capture. The attempted latch stays
        // set because a second owner cannot know whether the first call already cut.
        state = undefined;
    }

    try {
        runtime.marketplaceLogRocketPrincipalState = state;
    } catch (error) {
        // The caller still receives the state; owners that cannot read it fail closed.
    }

    return state;
}

export function removeStorageItem(storage, key) {
    try {
        storage?.removeItem(key);
    } catch (error) {
        // Blocked storage must always degrade to a missing cache.
    }
}

export function forgetEveryMarketplaceEntry(storage) {
    try {
        const keys = [];

        for (let index = 0; index < storage.length; index++) {
            const key = storage.key(index);

            if (typeof key === 'string' && key.startsWith(`${LOGROCKET_SESSION_CACHE_KEY_PREFIX}:`)) {
                keys.push(key);
            }
        }

        keys.forEach(key => removeStorageItem(storage, key));
    } catch (error) {
        // Some storage shims do not expose length/key; reads stay guarded anyway.
    }
}

/**
 * A shared browser that changed accounts must not keep recording user B into user A's
 * session: the replay would show two different people, and whichever sale is handed off
 * next would freeze the wrong one as its evidence.
 *
 * Two rules keep that boundary honest when things fail:
 *
 * - A MISSING marker is not "safely mine". It means nobody knows whose recording this
 *   browser carries - the first page after this feature deploys, cleared storage, or a
 *   recovery visit that deliberately cleared it - so the current recording is ended once
 *   BEFORE this principal is identified onto it.
 * - The marker only advances after the recording was actually ended. Recording the switch
 *   as done when it was not is what made a failure permanent: every later page saw "same
 *   principal" and B kept recording into A's session. A required switch the SDK could not
 *   perform is reported as ROTATION_FAILED so the caller suppresses identify/capture on
 *   this page, and the unchanged marker makes the next page with a working SDK retry the
 *   very same switch.
 *
 * Detecting the change needs somewhere to remember the previous principal. When storage
 * is unavailable - absent, unreadable, or a per-document stand-in that production
 * callers already translate to null via durableLocalStorage() - BOTH flavors
 * deliberately avoid rotation:
 * without a durable marker, cutting on every navigation would fragment every replay.
 * They return UNTRACKABLE instead, and the centralized permission decision suppresses
 * every identify/capture owner. This keeps the handoff working with honest-null evidence
 * without either repeated cuts or a recording attributed across an unknown boundary.
 *
 * @returns {string} PRINCIPAL_UNCHANGED | PRINCIPAL_ROTATED | PRINCIPAL_ROTATION_FAILED | PRINCIPAL_UNTRACKABLE
 */
export function rotateLogRocketPrincipalIfChanged(storage, logRocket, userId) {
    const principal = normalizeLogRocketId(userId);

    if (principal === null) {
        // Nobody is signed in on this page, so there is no principal to switch TO. The
        // marker is left alone: the next authenticated page still has to be able to see
        // that this browser was recording somebody else.
        return PRINCIPAL_UNCHANGED;
    }

    if (!storage) {
        return PRINCIPAL_UNTRACKABLE;
    }

    let previous = null;

    try {
        previous = storage.getItem(LOGROCKET_PRINCIPAL_KEY) ?? null;
    } catch (error) {
        return PRINCIPAL_UNTRACKABLE;
    }

    if (previous === principal) {
        return PRINCIPAL_UNCHANGED;
    }

    if (previous !== null) {
        // A known switch also orphans the previous person's cached entries. An unknown
        // previous does not purge: entries are user+loan-scoped and readers only consult
        // their own keys, so foreign ones are inert - the live recording is the risk.
        forgetEveryMarketplaceEntry(storage);
    }

    if (!endCurrentRecording(logRocket)) {
        return PRINCIPAL_ROTATION_FAILED;
    }

    try {
        storage.setItem(LOGROCKET_PRINCIPAL_KEY, principal);
    } catch (error) {
        // Read-only storage: the next DOCUMENT repeats the cut, while the per-document
        // latch prevents the deferred initializer from doing it again on this one. The
        // cut stays first because marking first and then failing the cut would record a
        // rotation that never happened - the original isolation defect.
    }

    return PRINCIPAL_ROTATED;
}

/**
 * The supported way to end whatever this browser is recording. Only a call that really
 * happened counts as ended: an absent SDK, an older build without the API, or a throwing
 * call all report failure so the caller does not mark the switch complete.
 */
function endCurrentRecording(logRocket) {
    if (typeof logRocket?.startNewSession !== 'function') {
        return false;
    }

    try {
        logRocket.startNewSession();
    } catch (error) {
        return false;
    }

    return true;
}
